import { useEffect, useState, useRef } from 'react';
import { Typography, Box, Container, Grid, Alert, Skeleton, Stack, Paper, IconButton, CircularProgress, Card, CardHeader, CardContent, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import {
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { FiltersPanel } from '@/components/filters/FiltersPanel';
import { SummaryCard } from '@/components/dashboard/SummaryCard';
import { RegionDrillDown } from '@/components/dashboard/RegionDrillDown';
import { ExecutiveDashboard } from '@/components/dashboard/ExecutiveDashboard';
import { useDashboardView } from '@/App';
import { DASHBOARD_MODES } from '@/components/Header/Header';
import { useFilterVisibility } from '@/contexts/FilterVisibilityContext';
import { RootState } from '@/store/store';
import {
  setSelectedProduct,
  setSelectedStatus,
  setSelectedMatrixType,
  setSelectedMatrixItem,
  setShowPerformanceMatrix,
  resetMatrixStates,
  fetchChannelPerformanceBreakdownStart,
  fetchSubchannelPerformanceBreakdownStart,
  fetchProductPerformanceBreakdownStart,
  fetchProductChannelDistributionStart,
  fetchChannelProductDistributionStart,
  fetchProductStatusDistributionStart,
  fetchChannelStatusDistributionStart,
  fetchSubchannelStatusDistributionStart,
  fetchProductConversionRateStart,
  fetchChannelConversionRateStart,
  fetchSubchannelConversionRateStart,
  FETCH_CHANNEL_PRODUCT_DISTRIBUTION_SUCCESS
} from '@/store/slices/dashboardSlice';
import { setCurrentFilters } from '@/store/slices/filtersSlice';
import { setSelectedRegion } from '@/store/slices/dashboardSlice';
import { PerformanceDataProvider } from '@/contexts/PerformanceDataContext';

import { fetchPerformanceData } from '@/store/epics/dashboardEpics';
import { fetchRegionMapStart } from '@/store/slices/regionMapSlice';
import { LOBPerformance, RegionData } from '@/types/dashboard';
import { BarChart } from '@/components/dashboard/BarChart';
import { StatusBarChart } from '@/components/dashboard/StatusBarChart';
import { ChannelStatusChart } from '@/components/dashboard/ChannelStatusChart';
import { SubchannelBarChart } from '@/components/dashboard/SubchannelBarChart';
import { DonutChart } from '@/components/dashboard/DonutChart';
import { ChannelPerformanceBreakdown } from '@/components/dashboard/ChannelPerformanceBreakdown';
import { ProductPerformanceBreakdown } from '@/components/dashboard/ProductPerformanceBreakdown';
import { SelectedFilters } from '@/types/filters';

import { 
  fetchProductChannelDistribution, 
  fetchChannelProductDistribution,
  fetchProductStatusDistribution,
  fetchChannelStatusDistribution,
  fetchSubchannelStatusDistribution,
  fetchProductConversionRate,
  fetchChannelConversionRate,
  fetchSubchannelConversionRate,
  ProductChannelResponse,
  ChannelProductResponse,
  ChannelStatusResponse,
  SubchannelStatusResponse,
  ProductConversionResponse,
  ChannelConversionResponse,
  SubchannelConversionResponse,
  ProductStatusResponse
} from '@/services/api';
import { SubChannelPerformanceBreakdown } from '@/components/dashboard/SubChannelPerformanceBreakdown';
import { MiniRegionMap } from '@/components/dashboard/MiniRegionMap';
import { RegionManagerDrillDown } from '@/components/dashboard/RegionManagerDrillDown';
import { fetchManagerBreakdown, ManagerBreakdownResponse } from '@/services/api';


// Chola Insurance theme colors
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#333333',
  lightBlue: '#0066CC',
  grey: '#F5F5F5',
  hover: '#004AAD' // Darker blue for hover states
};

export function Dashboard() {
  const dispatch = useDispatch();
  const { viewMode } = useDashboardView();
  const { showFilters, toggleFilters } = useFilterVisibility();
  
  // Select state from Redux store
  const {
    overallPerformance,
    lobPerformance,
    loading,
    error,
    selectedRegion,
    selectedProduct,
    selectedStatus,
    selectedMatrixType,
    selectedMatrixItem,
    showPerformanceMatrix
  } = useSelector((state: RootState) => state.dashboard);

  const { data: regionMapData, loading: regionMapLoading } = useSelector((state: RootState) => state.regionMap);

  // Transform region map data to match RegionData type
  const transformedRegionData = regionMapData?.region_data 
    ? Object.entries(regionMapData.region_data).reduce((acc: Record<string, RegionData>, [key, value]) => {
        acc[key] = {
          totalLeads: Number(value.total_leads),
          converted: Number(value.converted),
          conversionRate: Number(value.conversion_rate),
          managers: [] // Add managers data if available from the API
        };
        return acc;
      }, {})
    : {};

  const [productChannelData, setProductChannelData] = useState<ProductChannelResponse | null>(null);
  const [channelProductData, setChannelProductData] = useState<ChannelProductResponse | null>(null);
  const [productStatusData, setProductStatusData] = useState<ProductStatusResponse | null>(null);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [isLoadingDistribution, setIsLoadingDistribution] = useState(false);
  const [channelStatusData, setChannelStatusData] = useState<ChannelStatusResponse | null>(null);
  const [subchannelStatusData, setSubchannelStatusData] = useState<SubchannelStatusResponse | null>(null);
  const [productConversionData, setProductConversionData] = useState<ProductConversionResponse | null>(null);
  const [channelConversionData, setChannelConversionData] = useState<ChannelConversionResponse | null>(null);
  const [subchannelConversionData, setSubchannelConversionData] = useState<SubchannelConversionResponse | null>(null);
  const [currentFilters, setFiltersState] = useState<SelectedFilters | null>(null);
  const [regionManagerLoading, setRegionManagerLoading] = useState(false);
  const [regionManagerError, setRegionManagerError] = useState<string | null>(null);
  const [regionManagerData, setRegionManagerData] = useState<ManagerBreakdownResponse | null>(null);
  const [conversionViewMode, setConversionViewMode] = useState<'product' | 'channel' | 'subchannel'>('product');

  // Initial load
  const initialLoadRef = useRef(false);

  useEffect(() => {
    // Skip if already initialized
    if (initialLoadRef.current) {
      return;
    }
    
    initialLoadRef.current = true;

    // Include selectedRegion in initial API calls if available
    const initialFilters = selectedRegion 
      ? {
          selectedRegion,
          selectedMonth: undefined,
          selectedYear: undefined,
          selectedLOB: [] as string[],
          selectedProducts: [] as string[],
          selectedChannels: [] as string[],
          selectedSubChannels: [] as string[],
          dateRange: { startDate: null, endDate: null }
        } as SelectedFilters
      : undefined;
    
    dispatch(fetchPerformanceData(initialFilters));
    dispatch(fetchRegionMapStart(initialFilters));
    dispatch(fetchChannelPerformanceBreakdownStart());
    dispatch(fetchSubchannelPerformanceBreakdownStart());
    dispatch(fetchProductPerformanceBreakdownStart());
    dispatch(fetchProductChannelDistributionStart());
    dispatch(fetchChannelProductDistributionStart());
    dispatch(fetchProductStatusDistributionStart());
    dispatch(fetchChannelStatusDistributionStart());
    dispatch(fetchSubchannelStatusDistributionStart());
    dispatch(fetchProductConversionRateStart());
    dispatch(fetchChannelConversionRateStart());
    dispatch(fetchSubchannelConversionRateStart());
    fetchDistributionData(initialFilters as SelectedFilters | undefined);
  }, [dispatch, selectedRegion]);
  
  // Handle region changes separately
  useEffect(() => {
    // Skip initial load - that's handled by the effect above
    if (!initialLoadRef.current || !selectedRegion) {
      return;
    }
    
    const regionFilters = {
      selectedRegion,
      selectedMonth: undefined,
      selectedYear: undefined,
      selectedLOB: [] as string[],
      selectedProducts: [] as string[],
      selectedChannels: [] as string[],
      selectedSubChannels: [] as string[],
      dateRange: { startDate: null, endDate: null }
    } as SelectedFilters;
    
    // Don't log here to avoid console spam
    dispatch(fetchPerformanceData(regionFilters));
    dispatch(fetchRegionMapStart(regionFilters));
  }, [selectedRegion, dispatch]);

  const fetchDistributionData = async (filters?: SelectedFilters) => {
    try {
      setIsLoadingDistribution(true);
      const [
        productChannelResponse, 
        channelProductResponse, 
        productStatusResponse, 
        channelStatusResponse,
        subchannelStatusResponse,
        productConversionResponse,
        channelConversionResponse,
        subchannelConversionResponse
      ] = await Promise.all([
        fetchProductChannelDistribution(true, filters),
        fetchChannelProductDistribution(true, filters),
        fetchProductStatusDistribution(true, filters),
        fetchChannelStatusDistribution(true, filters),
        fetchSubchannelStatusDistribution(true, filters),
        fetchProductConversionRate(true, filters),
        fetchChannelConversionRate(true, filters),
        fetchSubchannelConversionRate(true, filters)
      ]);
      setProductChannelData(productChannelResponse);
      setChannelProductData(channelProductResponse);
      setProductStatusData(productStatusResponse);
      setChannelStatusData(channelStatusResponse);
      setSubchannelStatusData(subchannelStatusResponse);
      setProductConversionData(productConversionResponse);
      setChannelConversionData(channelConversionResponse);
      setSubchannelConversionData(subchannelConversionResponse);
      
      // Update Redux store with channelProductResponse to keep it in sync with the local state
      // This ensures that components using Redux data (like the BarChart in channel view mode)
      // will have the latest data when filters change
      dispatch({
        type: FETCH_CHANNEL_PRODUCT_DISTRIBUTION_SUCCESS,
        payload: channelProductResponse
      });
    } catch (error) {
      console.error('Error fetching distribution data:', error);
    } finally {
      setIsLoadingDistribution(false);
    }
  };

  const handleBarClick = (item: string, type: 'product' | 'channel' | 'status' | 'subchannel') => {
    // Handle the click based on the type
    if (type === 'product') {
      // Handle product click
    } else if (type === 'channel') {
      // Handle channel click
    } else if (type === 'status') {
      // Handle status click
    } else if (type === 'subchannel') {
      // Handle subchannel click
    }
  };

  // Process chart data with proper type handling
  const processChartData = () => {
    if (!productChannelData || !channelProductData || !productStatusData || !channelStatusData || !subchannelStatusData) 
      return { 
        productChannelChartData: [], 
        statusChartData: [], 
        statusProductChartData: [], 
        channelStatusChartData: [], 
        subchannelStatusChartData: [],
        conversionData: [],
        channelConversionData: [],
        subChannelConversionData: []
      };

    // Process product-channel data
    const productChannelChartData = productChannelData.products.map(product => ({
      name: product.product_group,
      total: parseInt(product.total_leads),
      conversion_rate: parseFloat(product.conversion_rate),
      converted_leads: parseInt(product.converted_leads),
      channels: Object.entries(product.channels).map(([channelName, channelData]) => ({
        name: channelName,
        value: parseInt(channelData.total_leads),
        conversion_rate: parseFloat(channelData.conversion_rate),
        converted_leads: parseInt(channelData.converted_leads),
        contribution_rate: parseFloat(channelData.contribution_rate)
      }))
    }));

    // Process product status data
    const statusChartData = productStatusData.products.map(product => {
      // Calculate conversion rate and converted leads
      const totalLeads = parseInt(product.total_leads);
      const convertedLeads = parseInt(product.converted_leads);
      const conversionRate = totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0;
      
      return {
        name: product.product_group,
        total: totalLeads,
        conversion_rate: conversionRate,
        converted_leads: convertedLeads,
        statuses: Object.entries(product.statuses).map(([statusName, statusData]) => ({
          name: statusName,
          value: parseInt(statusData.total_leads),
          percentage: parseFloat(statusData.percentage),
          conversion_rate: parseFloat(statusData.conversion_rate),
          converted_leads: parseInt(statusData.converted_leads)
        }))
      };
    });

    // Process channel-status data
    const channelStatusChartData = channelStatusData.channels.map(channel => ({
      name: channel.channel,
      total: parseInt(channel.total_leads),
      statuses: Object.entries(channel.statuses).map(([statusName, statusData]) => ({
        name: statusName,
        value: parseInt(statusData.total_leads),
        percentage: parseFloat(statusData.percentage),
        conversion_rate: parseFloat(statusData.conversion_rate),
        converted_leads: parseInt(statusData.converted_leads)
      }))
    }));

    // Process subchannel-status data
    const subchannelStatusChartData = subchannelStatusData.subchannels.map(subchannel => ({
      name: subchannel.subchannel,
      total: parseInt(subchannel.total_leads),
      statuses: Object.entries(subchannel.statuses).map(([statusName, statusData]) => ({
        name: statusName,
        value: parseInt(statusData.total_leads),
        percentage: parseFloat(statusData.percentage),
        conversion_rate: parseFloat(statusData.conversion_rate),
        converted_leads: parseInt(statusData.converted_leads)
      }))
    }));

    // Process conversion rate data for products
    const productConversionChartData = productConversionData ? productConversionData.products.map(product => ({
      id: product.product_group,
      label: product.product_group,
      value: parseFloat(product.conversion_rate),
      formattedValue: product.conversion_rate + '%',
      data: {
        totalLeads: parseInt(product.total_leads),
        convertedLeads: parseInt(product.converted_leads)
      }
    })).slice(0, 10) : [];

    // Process conversion rate data for channels
    const channelConversionChartData = channelConversionData ? channelConversionData.channels.map(channel => ({
      id: channel.channel,
      label: channel.channel,
      value: parseFloat(channel.conversion_rate),
      formattedValue: channel.conversion_rate + '%',
      data: {
        totalLeads: parseInt(channel.total_leads),
        convertedLeads: parseInt(channel.converted_leads)
      }
    })) : [];

    // Process conversion rate data for subchannels
    const subchannelConversionChartData = subchannelConversionData ? subchannelConversionData.subchannels.map(subchannel => ({
      id: subchannel.subchannel,
      label: subchannel.subchannel,
      value: parseFloat(subchannel.conversion_rate),
      formattedValue: subchannel.conversion_rate + '%',
      data: {
        totalLeads: parseInt(subchannel.total_leads),
        convertedLeads: parseInt(subchannel.converted_leads),
        channel: subchannel.channel
      }
    })) : [];

    return { 
      productChannelChartData, 
      statusChartData, 
      statusProductChartData: [], 
      channelStatusChartData, 
      subchannelStatusChartData,
      conversionData: productConversionChartData,
      channelConversionData: channelConversionChartData,
      subChannelConversionData: subchannelConversionChartData
    };
  };

  // Handle filter changes
  const handleFiltersChange = (filters: SelectedFilters) => {
    // Preserve the selectedRegion if it exists in current filters
    const updatedFilters: SelectedFilters = {
      ...filters,
      selectedRegion: filters.selectedRegion || selectedRegion || currentFilters?.selectedRegion || undefined
    };
    
    setFiltersState(updatedFilters);
    
    // Update Redux store with the new filters 
    dispatch(setCurrentFilters({
      selectedMonth: filters.selectedMonth ? [parseInt(filters.selectedMonth)] : [],
      selectedYear: filters.selectedYear ? [parseInt(filters.selectedYear)] : [],
      selectedLOB: filters.selectedLOB,
      selectedProducts: filters.selectedProducts,
      selectedSubProducts: filters.selectedSubProducts,
      selectedChannels: filters.selectedChannels,
      selectedSubChannels: filters.selectedSubChannels
    }));
    
    // Create a copy with the correct types
    const apiFilters = { ...updatedFilters };
    
    // Reload data with new filters
    fetchDistributionData(apiFilters);
    
    // Update performance data with new filters
    dispatch(fetchPerformanceData(apiFilters));
    
    // Update region map with new filters 
    dispatch(fetchRegionMapStart(apiFilters));

    // Fetch performance breakdowns with new filters
    dispatch(fetchProductPerformanceBreakdownStart(apiFilters));
    dispatch(fetchChannelPerformanceBreakdownStart(apiFilters));
    dispatch(fetchSubchannelPerformanceBreakdownStart(apiFilters));
  };

  const handleProductClick = (product: string) => {
    dispatch(setSelectedProduct(product));
  };

  const handleStatusClick = (status: string) => {
    dispatch(setSelectedStatus(status));
  };

  const handleRegionSelect = (region: string) => {
    // Store selected region in Redux
    dispatch(setSelectedRegion(region));
    
    // Create filters with the selected region while preserving ALL existing filters
    const regionFilters: SelectedFilters = {
      // Start with default empty values
      selectedMonth: undefined,
      selectedYear: undefined,
      selectedLOB: [],
      selectedProducts: [],
      selectedSubProducts: [],
      selectedChannels: [],
      selectedSubChannels: [],
      dateRange: { startDate: null, endDate: null },
      // Spread current filters (this overrides defaults with actual values)
      ...(currentFilters || {}),
      // Set the new region
      selectedRegion: region
    };
    
    // For debugging - log what filters we're applying
    
    // Update local state with the new filters
    setFiltersState(regionFilters);
    setSelectedRegion(region);
    
    // Update Redux with the new filters - make sure to include ALL filter values
    dispatch(setCurrentFilters({
      selectedMonth: regionFilters.selectedMonth ? [parseInt(regionFilters.selectedMonth)] : [],
      selectedYear: regionFilters.selectedYear ? [parseInt(regionFilters.selectedYear)] : [],
      selectedLOB: regionFilters.selectedLOB || [],
      selectedProducts: regionFilters.selectedProducts || [],
      selectedSubProducts: regionFilters.selectedSubProducts || [],
      selectedChannels: regionFilters.selectedChannels || [],
      selectedSubChannels: regionFilters.selectedSubChannels || [],
      selectedRegion: region ? [region] : [] // Convert to array to match the expected type
    }));
    
    // Create a filter format that's compatible with fetchPerformanceData
    const performanceFilters = {
      ...regionFilters
    };
    
    // IMPORTANT: Directly fetch distribution data with the updated filters
    fetchDistributionData(regionFilters);
    
    // Fetch all dashboard data with the new filters
    dispatch(fetchPerformanceData(performanceFilters));
    dispatch(fetchRegionMapStart(regionFilters));
    
    // These Redux dispatches trigger the epics, but may not update state immediately
    // We've already directly fetched the distribution data above to ensure immediate UI update
    dispatch(fetchProductChannelDistributionStart());
    dispatch(fetchChannelProductDistributionStart());
    dispatch(fetchProductStatusDistributionStart());
    dispatch(fetchChannelStatusDistributionStart());
    dispatch(fetchSubchannelStatusDistributionStart());
    
    // Fetch conversion rate data
    dispatch(fetchProductConversionRateStart());
    dispatch(fetchChannelConversionRateStart());
    dispatch(fetchSubchannelConversionRateStart());
    
    // Fetch performance breakdown data
    dispatch(fetchProductPerformanceBreakdownStart());
    dispatch(fetchChannelPerformanceBreakdownStart());
    dispatch(fetchSubchannelPerformanceBreakdownStart());
    
    // Reset any matrix states
    dispatch(resetMatrixStates());
  };

  const renderSummaryCards = () => {
    if (!overallPerformance) return null;
  
    return (
      <Stack spacing={2}>
        <SummaryCard
          title="Total Base"
          value={overallPerformance.total_leads}
          icon={<PeopleIcon />}
          lobData={lobPerformance.map((lob: LOBPerformance) => ({
            label: lob.lob,
            value: lob.total_leads
          }))}
        />
        <SummaryCard
          title="Converted NOP"
          value={overallPerformance.converted_leads}
          icon={<TrendingUpIcon />}
          lobData={lobPerformance.map((lob: LOBPerformance) => ({
            label: lob.lob,
            value: lob.converted_leads
          }))}
        />
        <SummaryCard
          title="Conversion %"
          value={overallPerformance.conversion_rate}
          icon={<SpeedIcon />}
          lobData={lobPerformance.map((lob: LOBPerformance) => ({
            label: lob.lob,
            value: lob.conversion_rate
          }))}
        />
      </Stack>
    );
  };

  const renderCharts = () => {
    const { productChannelChartData, statusChartData, statusProductChartData, channelStatusChartData, subchannelStatusChartData, conversionData, channelConversionData, subChannelConversionData } = processChartData();

    if (isLoadingDistribution) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={450} />
            </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={450} />
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <BarChart
            title="Total Base by Product and Channel"
            data={productChannelChartData}
            onBarClick={handleBarClick}
          />
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ 
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            overflow: 'visible',
            position: 'relative',
            mb: 2
          }}>
            <CardHeader
              title={
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    fontWeight: 700, 
                    color: CHOLA_COLORS.primary,
                    fontSize: '1.25rem',
                    letterSpacing: '0.01em',
                    lineHeight: 1.3
                  }}
                >
                  Lead Disposition Analysis
                </Typography>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <BarChart
                    title="Product Status Distribution"
                    data={statusChartData}
                    onBarClick={handleBarClick}
                    showToggle={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ChannelStatusChart
                    title="Channel Status Distribution"
                    data={channelStatusChartData}
                    onBarClick={handleBarClick}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SubchannelBarChart
                    title="Subchannel Status Distribution"
                    data={subchannelStatusChartData}
                    onBarClick={handleBarClick}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    overflow: 'visible',
                    position: 'relative'
                  }}>
                    <CardHeader
                      title={
                        <Typography 
                          variant="h6" 
                          component="h2"
                          sx={{ 
                            fontWeight: 700, 
                            color: CHOLA_COLORS.primary,
                            fontSize: '1.25rem',
                            letterSpacing: '0.01em',
                            lineHeight: 1.3
                          }}
                        >
                          Conversion % Analysis
                        </Typography>
                      }
                      action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <ToggleButtonGroup
                            value={conversionViewMode}
                            exclusive
                            onChange={handleConversionViewChange}
                            size="small"
                            sx={{ 
                              '& .MuiToggleButton-root': {
                                px: 2,
                                py: 0.5,
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#475569',
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: `${CHOLA_COLORS.primary}15`,
                                  color: CHOLA_COLORS.primary,
                                  '&:hover': {
                                    backgroundColor: `${CHOLA_COLORS.primary}25`,
                                  },
                                },
                              },
                              '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
                                borderRadius: '4px 0 0 4px',
                              },
                              '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                                borderRadius: '0 4px 4px 0',
                              },
                            }}
                          >
                            <ToggleButton 
                              value="product"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              By Product
                            </ToggleButton>
                            {subChannelConversionData && subChannelConversionData.length > 0 && (
                              <ToggleButton 
                                value="subchannel"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                By SubChannel
                              </ToggleButton>
                            )}
                            <ToggleButton 
                              value="channel"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              By Channel
                            </ToggleButton>
                          </ToggleButtonGroup>
                          <IconButton 
                            onClick={() => {
                              // Handle export based on the current view
                              // You can implement this functionality as needed
                            }}
                            size="small"
                            aria-label="Export data"
                            title="Export data as CSV"
                            sx={{ 
                              color: CHOLA_COLORS.primary,
                              '&:hover': {
                                backgroundColor: `${CHOLA_COLORS.primary}10`
                              }
                            }}
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                      sx={{
                        '& .MuiCardHeader-action': {
                          margin: 0,
                          alignSelf: 'center',
                        },
                      }}
                    />
                    <CardContent sx={{ 
                      height: 450,
                      p: 2,
                      '&:last-child': {
                        pb: 2
                      }
                    }}>
                      {conversionViewMode === 'product' && (
                        <DonutChart
                          title=""
                          data={conversionData}
                          compact={false}
                        />
                      )}
                      {conversionViewMode === 'channel' && (
                        <DonutChart
                          title=""
                          data={channelConversionData}
                          compact={false}
                        />
                      )}
                      {conversionViewMode === 'subchannel' && subChannelConversionData && subChannelConversionData.length > 0 && (
                        <DonutChart
                          title=""
                          data={subChannelConversionData}
                          compact={false}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Load manager data when region changes or on initial load
  useEffect(() => {
    // Load manager data on initial load or when region/filters change
    loadManagerData(selectedRegion || 'all');
  }, [selectedRegion, currentFilters]);

  const loadManagerData = async (region: string) => {
    try {
      setRegionManagerLoading(true);
      setRegionManagerError(null);
      
      // Create a complete filters object with proper type conversion
      const apiFilters: any = {};


      
      if (currentFilters) {
        // Convert string months to numbers
        if (currentFilters.selectedMonth?.length) {
          apiFilters.selectedMonth = currentFilters.selectedMonth
        }
        
        // Convert string years to numbers
        if (currentFilters.selectedYear?.length) {
          apiFilters.selectedYear = currentFilters.selectedYear
        }
        
        // Copy other filter properties
        if (currentFilters.selectedLOB?.length) apiFilters.selectedLOB = currentFilters.selectedLOB;
        if (currentFilters.selectedProducts?.length) apiFilters.selectedProducts = currentFilters.selectedProducts;
        if (currentFilters.selectedChannels?.length) apiFilters.selectedChannels = currentFilters.selectedChannels;
        if (currentFilters.selectedSubChannels?.length) apiFilters.selectedSubChannels = currentFilters.selectedSubChannels;
        if (currentFilters.dateRange) apiFilters.dateRange = currentFilters.dateRange;
        if (currentFilters.selectedSubProducts?.length) apiFilters.selectedSubProducts = currentFilters.selectedSubProducts;
        if (currentFilters.selectedRegion?.length) apiFilters.selectedRegion = currentFilters.selectedRegion;
      }
      
      const response = await fetchManagerBreakdown(
        undefined, // Default employee_id 
        undefined, // Default role
        '[]', // Default hierarchy_path
        apiFilters
      );
      
      setRegionManagerData(response);
    } catch (error) {
      console.error('Error loading manager data:', error);
      setRegionManagerError('Failed to load manager data. Please try again later.');
    } finally {
      setRegionManagerLoading(false);
    }
  };

  // Handle tab change for conversion rate views
  const handleConversionViewChange = (
    event: React.MouseEvent<HTMLElement>, 
    newView: 'product' | 'channel' | 'subchannel' | null
  ) => {
    if (newView !== null) {
      setConversionViewMode(newView);
    }
  };

  return (
    <Container maxWidth={false} sx={{ 
      py: 2,
      height: '100%',
      overflowY: 'auto',
      overflowX: 'hidden'
    }}>
      {/* Debug indicator - remove this later */}
      {/* <Box sx={{ 
        position: 'fixed', 
        top: 80, 
        right: 20, 
        zIndex: 10000,
        bgcolor: 'rgba(0,0,0,0.8)',
        color: 'white',
        p: 1,
        borderRadius: 1,
        fontSize: '12px'
      }}>
        Filters: {showFilters ? 'SHOWN' : 'HIDDEN'}
        <Button 
          size="small" 
          variant="contained" 
          onClick={toggleFilters}
          sx={{ ml: 1, fontSize: '10px' }}
        >
          Toggle
        </Button>
      </Box> */}
      
      {/* Show FiltersPanel only when showFilters is true */}
      {showFilters && (
        <Box sx={{ 
          display: { xs: 'block', md: 'block' },
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          borderRadius: 1,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}>
          <FiltersPanel onFiltersChange={handleFiltersChange} compact={true} />
        </Box>
      )}
      <div id="main-content">
        {viewMode === DASHBOARD_MODES.EXECUTIVE ? (
          <ExecutiveDashboard 
          dashboardData={{
            totalLeads: Number(overallPerformance?.total_leads) || 0,
            statusDistribution: {},
            statusProductBreakdown: {},
            productDistribution: {},
            channelDistribution: {},
            channelStatusBreakdown: {},
            subChannelStatusBreakdown: {},
            regionData: transformedRegionData,
            lobMetrics: lobPerformance.reduce((acc: Record<string, any>, lob: LOBPerformance) => ({
              ...acc,
              [lob.lob]: {
                totalLeads: Number(lob.total_leads),
                converted: Number(lob.converted_leads),
                convertedLeads: Number(lob.converted_leads),
                conversionRate: Number(lob.conversion_rate)
              }
            }), {})
          }}
            loading={loading}
            error={error}
            onRegionSelect={handleRegionSelect}
            onProductClick={handleProductClick}
            onStatusClick={handleStatusClick}
            onBarClick={handleBarClick}
            onFiltersChange={handleFiltersChange}
            selectedRegion={selectedRegion || undefined}
          />
        ) : (
          <>
            {error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : (
              <>
                {/* Main Content Grid */}
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  {/* Left Side - Summary Cards */}
                  <Grid item xs={12} md={4}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mb: 3,
                        fontWeight: 600,
                        color: CHOLA_COLORS.primary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <SpeedIcon sx={{ color: CHOLA_COLORS.secondary }} />
                      Overall Performance
                    </Typography>
                    {loading ? (
                      <Stack spacing={2}>
                        {[1, 2, 3].map((item) => (
                          <Skeleton key={item} variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
                        ))}
                      </Stack>
                    ) : (
                      renderSummaryCards()
                    )}
                  </Grid>

                  {/* Right Side - Region Drill Down */}
                <Grid item xs={12} md={8}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mb: 2,
                        fontWeight: 600,
                        color: CHOLA_COLORS.primary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <TrendingUpIcon sx={{ color: CHOLA_COLORS.secondary }} />
                      State Performance Map
                    </Typography>
                    <Paper 
                      sx={{ 
                        p: 2,
                        height: 650,
                        borderRadius: 2,
                        boxShadow: `0 0 20px ${CHOLA_COLORS.primary}15`,
                        border: `1px solid ${CHOLA_COLORS.grey}`,
                        overflow: 'hidden'
                      }}
                    >
                    {regionMapLoading ? (
                        <Skeleton variant="rectangular" height={270} />
                      ) : (
                        <RegionDrillDown
                          regionData={transformedRegionData || {}}
                          onRegionSelect={handleRegionSelect}
                        />
                      )}
                    </Paper>
                </Grid>


                  {/* Charts Section - Full Width */}
                <Grid item xs={12}>
                    <Paper 
                      sx={{ 
                        p: 3,
                        borderRadius: 2,
                        boxShadow: `0 0 20px ${CHOLA_COLORS.primary}15`,
                        border: `1px solid ${CHOLA_COLORS.grey}`
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 3
                      }}>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: 600,
                            color: CHOLA_COLORS.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <AssessmentIcon sx={{ color: CHOLA_COLORS.secondary }} />
                          Base distribution analysis
                        </Typography>
                      </Box>
                      {isLoadingDistribution ? (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Skeleton variant="rectangular" height={450} />
                            </Grid>
                          <Grid item xs={12}>
                            <Skeleton variant="rectangular" height={450} />
                          </Grid>
                        </Grid>
                      ) : (
                        renderCharts()
                      )}
                    </Paper>
                  </Grid>


                  {!regionManagerLoading && !regionManagerError && (
                    <>
                    {/* Channel Performance Breakdown */}
                    <Grid item xs={12}>
                              <Paper 
                                elevation={0} 
                                sx={{ 
                                  p: 3, 
                                  borderRadius: 2, 
                                  border: '1px solid', 
                                  borderColor: 'divider',
                                  mb: 3
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="h6" fontWeight="bold" color="primary">
                                    Channel Performance Breakdown
                                  </Typography>
                                  <IconButton 
                                    onClick={() => {
                                      const exportButton = document.getElementById('channel-performance-export-btn');
                                      if (exportButton) {
                                        exportButton.click();
                                      }
                                    }}
                                    title="Export data"
                                    size="small"
                                    sx={{ color: CHOLA_COLORS.primary }}
                                  >
                                    <FileDownloadIcon />
                                  </IconButton>
                            </Box>
                                <ChannelPerformanceBreakdown compact={false} />
                              </Paper>
                            </Grid>    


                            {/* Sub Channel Performance Breakdown - Do not delete this */}
                            {/* <Grid item xs={12}>
                            <Paper 
                                elevation={0} 
                              sx={{ 
                                p: 3,
                                borderRadius: 2,
                                  border: '1px solid', 
                                  borderColor: 'divider',
                                  mb: 3
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="h6" fontWeight="bold" color="primary">
                                    Sub Channel Performance Breakdown
                                  </Typography>
                                  <IconButton 
                                    onClick={() => {
                                      const exportButton = document.getElementById('subchannel-performance-export-btn');
                                      if (exportButton) {
                                        exportButton.click();
                                      }
                                    }}
                                    title="Export data"
                                    size="small"
                                    sx={{ color: CHOLA_COLORS.primary }}
                                  >
                                    <FileDownloadIcon />
                                  </IconButton>
                                </Box>
                                <SubChannelPerformanceBreakdown compact={false} />
                              </Paper>
                            </Grid> */}

                            {/* Product Performance Breakdown */}
                            {/* <Grid item xs={12}>
                              <Paper 
                                elevation={0} 
                                  sx={{ 
                                  p: 3, 
                                  borderRadius: 2, 
                                  border: '1px solid', 
                                  borderColor: 'divider',
                                  mb: 3
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="h6" fontWeight="bold" color="primary">
                                    Product Performance Breakdown
                                </Typography>
                                <IconButton 
                                  onClick={() => {
                                      const exportButton = document.getElementById('product-performance-export-btn');
                                    if (exportButton) {
                                      exportButton.click();
                                    }
                                  }}
                                  title="Export data"
                                  size="small"
                                  sx={{ color: CHOLA_COLORS.primary }}
                                >
                                  <FileDownloadIcon />
                                </IconButton>
                              </Box>
                                <ProductPerformanceBreakdown compact={false} />
                              </Paper>
                            </Grid> */}
                            </>
                            )}

<Grid item xs={12}>
  {regionManagerLoading && (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress sx={{ color: CHOLA_COLORS.primary }} />
    </Box>
  )}

  {regionManagerError && (
    <Alert severity="error" sx={{ mb: 3 }}>
      {regionManagerError}
    </Alert>
  )}

  {/* Display RegionManagerDrillDown regardless of whether regionManagerData exists */}
  <Paper 
    sx={{ 
      p: 3,
      borderRadius: 2,
      boxShadow: `0 0 20px ${CHOLA_COLORS.primary}15`,
      border: `1px solid ${CHOLA_COLORS.grey}`
    }}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}
    >
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 600,
          color: CHOLA_COLORS.primary,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <PeopleIcon sx={{ color: CHOLA_COLORS.secondary }} />
        Manager Performance Breakdown {selectedRegion ? `- ${selectedRegion}` : ''}
      </Typography>
      <IconButton 
        onClick={() => {
          // Get reference to the RegionManagerDrillDown component's export function
          const exportButton = document.getElementById('region-manager-export-btn');
          if (exportButton) {
            exportButton.click();
          }
        }}
        title="Export data"
        size="small"
        sx={{ color: CHOLA_COLORS.primary }}
      >
        <FileDownloadIcon />
      </IconButton>
    </Box>
    <RegionManagerDrillDown 
      region={selectedRegion || 'all'}
      initialData={regionManagerData}
      filters={{
        selectedMonths: currentFilters?.selectedMonth,
        selectedYears: currentFilters?.selectedYear,
        selectedLOB: currentFilters?.selectedLOB,
        selectedProducts: currentFilters?.selectedProducts,
        selectedChannels: currentFilters?.selectedChannels,
        selectedSubChannels: currentFilters?.selectedSubChannels,
        dateRange: currentFilters?.dateRange,
        selectedRegion: currentFilters?.selectedRegion,
        selectedSubProducts: currentFilters?.selectedSubProducts
      }}
    />
  </Paper>
</Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </div>
    </Container>
  );
} 